// {{key}} substitution + the catalog of placeholders the editor can offer.
import { TRANSACTION_TEMPLATE_META } from "./transactionEmailSequence";

const TRANSACTION_IDS = Object.keys(TRANSACTION_TEMPLATE_META);

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

// Time-aware greeting for {{greeting}}: morning until noon, afternoon until
// 5 PM, evening after. Hour is injected so tests don't depend on the clock.
export function greetingForHour(hour) {
  const h = Number(hour);
  if (!Number.isFinite(h) || h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

// Literal greetings become the time-aware {{greeting}} token, so a template
// typed or pasted with "Good Morning/Afternoon" adapts to the hour it's sent
// (Henry, 2026-09-01: "when we make a templete it knows if its morning or
// afternoon"). Two rules keep it safe:
//   1. The slash placeholder forms (Good Morning/Afternoon[/Evening]) are
//      unambiguous — replaced wherever they appear.
//   2. A single "Good Morning|Afternoon|Evening" is replaced only at the very
//      start of the text, where it can only be a salutation — "have a good
//      morning" mid-sentence stays put.
export function normalizeGreetings(text) {
  if (!text) return "";
  return String(text)
    .replace(/good\s+morning\s*\/\s*afternoon(\s*\/\s*evening)?/gi, "{{greeting}}")
    .replace(/^(\s*)good\s+(morning|afternoon|evening)/i, "$1{{greeting}}");
}

export function interpolate(template, data) {
  if (template === null || template === undefined) return "";
  const noData = data === null || data === undefined;
  const d = noData ? {} : data;
  return String(template).replace(PLACEHOLDER_RE, (whole, key) => {
    if (noData) return "";
    if (!Object.prototype.hasOwnProperty.call(d, key)) return whole;
    const v = d[key];
    return v === null || v === undefined ? "" : String(v);
  });
}

// Groups carry an optional `templates` whitelist — only those templates see
// the group. When `templates` is missing, the group shows everywhere.
export const PLACEHOLDER_GROUPS = [
  {
    label: "Transaction",
    templates: TRANSACTION_IDS,
    items: [
      { key: "greeting",        display: "Greeting (time-aware)", icon: "🕐" },
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
      { key: "clientNames",     display: "Client names",     icon: "👥" },
      { key: "sellerNames",     display: "Seller names",     icon: "👤" },
      { key: "buyerNames",      display: "Buyer names",      icon: "👤" },
      { key: "agentName",       display: "Agent name",       icon: "✍️" },
    ],
  },
  {
    label: "Open House",
    templates: [
      "scheduling-seller", "scheduling-agent",
      "update-seller", "update-agent",
      "cancellation-seller", "cancellation-agent",
      "recap-seller",
    ],
    items: [
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
      { key: "date",             display: "Date",             icon: "🗓" },
      { key: "time",             display: "Time",             icon: "⏱" },
    ],
  },
  {
    label: "Seller",
    templates: [
      "scheduling-seller", "update-seller", "cancellation-seller",
      "recap-seller",
    ],
    items: [
      { key: "sellerFirstName", display: "Seller first name", icon: "👤" },
      { key: "sellerName",      display: "Seller full name",  icon: "👤" },
    ],
  },
  {
    label: "Hosting Agent",
    templates: [
      "scheduling-seller", "scheduling-agent",
      "update-seller", "update-agent",
      "cancellation-seller", "cancellation-agent",
    ],
    items: [
      { key: "hostingAgentFirstName", display: "Hosting agent first name", icon: "🧑" },
      { key: "hostingAgentName",      display: "Hosting agent full name",  icon: "🧑" },
    ],
  },
  {
    label: "Feedback",
    templates: ["recap-seller"],
    items: [
      { key: "groups",         display: "# of groups",        icon: "👥" },
      { key: "secondShowings", display: "# of 2nd showings",  icon: "🔁" },
      { key: "agentFeedback",  display: "Agent feedback",     icon: "💬" },
      { key: "buyerFeedback",  display: "Buyer feedback",     icon: "💬" },
      { key: "otherComments",  display: "Other comments",     icon: "💬" },
    ],
  },
  // Open House hosting requests (public hosting page + seller approval; 09/16/2026).
  // Every seller on the file is greeted at once ({{sellerFirstNames}}: "Kayla and Jake").
  // Two groups, split by who builds the email, so a template is only ever offered a
  // chip its builder supplies: interpolate() above leaves an unknown key as literal
  // "{{...}}" text, which would land in the seller's inbox verbatim.
  //   - The seller ask is ONE email carrying SEVERAL proposals (each with its own host,
  //     date and time listed in the body), so it has no single host, date or time chip.
  //   - The three confirmations are about exactly one request and get the full set;
  //     they are sent by functions-api/ohRequestEmails.js, whose data bag carries every
  //     key below (senderFirstName = the listing agent's first name).
  {
    label: "Open House Requests",
    templates: ["oh-request-seller"],
    items: [
      { key: "propertyAddress",       display: "Property Address",             icon: "📍" },
      { key: "sellerFirstNames",      display: "Seller first names",           icon: "👥" },
      { key: "senderFirstName",       display: "Your first name",              icon: "✍️" },
    ],
  },
  {
    label: "Open House Request Confirmations",
    templates: [
      "oh-request-host-confirmed", "oh-request-seller-confirmed", "oh-request-agent-declined",
    ],
    items: [
      { key: "propertyAddress",       display: "Property Address",             icon: "📍" },
      { key: "sellerFirstNames",      display: "Seller first names",           icon: "👥" },
      { key: "hostingAgentFirstName", display: "Hosting agent first name",     icon: "🧑" },
      { key: "hostingAgentName",      display: "Hosting agent full name",      icon: "🧑" },
      { key: "date",                  display: "Date",                         icon: "🗓" },
      { key: "time",                  display: "Time",                         icon: "⏱" },
      { key: "agentFirstName",        display: "Requesting agent first name",  icon: "🧑" },
      { key: "senderFirstName",       display: "Your first name",              icon: "✍️" },
    ],
  },
  // Open house feedback reminders to the hosting agent (spec 2026-09-19 Part 3). Sent
  // by the functions-api cron (openHouseFeedbackReminders.js), whose data bag carries
  // exactly these four keys; any other key is blanked there.
  {
    label: "Feedback Reminder",
    templates: [
      "oh-feedback-reminder-1", "oh-feedback-reminder-5",
      "oh-feedback-reminder-12", "oh-feedback-reminder-24",
    ],
    items: [
      { key: "hostFirstName", display: "Hosting agent first name", icon: "🧑" },
      { key: "address",       display: "Address (street)",         icon: "📍" },
      { key: "when",          display: "Date and time",            icon: "🗓" },
      { key: "feedbackLink",  display: "Feedback link",            icon: "🔗" },
    ],
  },
  // ===== Client touchpoints + marketing templates =====
  {
    label: "Contact",
    templates: [
      "birthday", "home-anniversary", "closing-day", "welcome",
      "just-listed", "just-sold",
    ],
    items: [
      { key: "contactFirstName", display: "Contact first name", icon: "👤" },
      { key: "contactName",      display: "Contact full name",  icon: "👤" },
    ],
  },
  {
    label: "Property",
    templates: [
      "home-anniversary", "closing-day", "just-listed", "just-sold",
    ],
    items: [
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
    ],
  },
  {
    label: "Anniversary",
    templates: ["home-anniversary"],
    items: [
      { key: "yearsInHome", display: "Years in home", icon: "🎉" },
    ],
  },
  {
    label: "Sender",
    templates: [
      "birthday", "home-anniversary", "closing-day", "welcome",
      "just-listed", "just-sold",
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "staging-appraisal-vendor",
      "photographer-vendor", "pre-inspection-vendor",
    ],
    items: [
      { key: "senderFirstName", display: "Your first name", icon: "✍️" },
      { key: "senderName",      display: "Your full name",  icon: "✍️" },
    ],
  },
  // ===== Vendor Notifications =====
  {
    label: "Vendor",
    templates: [
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor", "staging-appraisal-vendor",
    ],
    items: [
      { key: "vendorName", display: "Vendor name", icon: "🏠" },
    ],
  },
  {
    label: "Deal",
    templates: [
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor", "staging-appraisal-vendor",
    ],
    items: [
      { key: "propertyAddress", display: "Property Address", icon: "📍" },
      { key: "clients",          display: "Clients",         icon: "👥" },
      { key: "closingDate",      display: "Closing Date",    icon: "🗓" },
      { key: "targetListDate",   display: "Target List Date", icon: "🗓" },
    ],
  },
  {
    label: "Schedule",
    templates: ["staging-install-vendor"],
    items: [
      { key: "installDate", display: "Install Date", icon: "🗓" },
    ],
  },
  {
    label: "Schedule",
    templates: ["staging-destage-vendor"],
    items: [
      { key: "destageDate", display: "Destage Date", icon: "🗓" },
    ],
  },
  {
    label: "Schedule",
    templates: ["photographer-vendor"],
    items: [
      { key: "photoshootDate", display: "Photoshoot Date", icon: "🗓" },
    ],
  },
  {
    label: "Inspections",
    templates: ["pre-inspection-vendor"],
    items: [
      { key: "inspectionList", display: "Inspection list", icon: "📋" },
    ],
  },
  {
    label: "Lockbox",
    templates: [
      "staging-consult-vendor", "staging-install-vendor", "staging-destage-vendor",
      "photographer-vendor", "pre-inspection-vendor",
    ],
    items: [
      // Two lockboxes (Henry, 2026-09-25): a listing can hold one Push Button AND one Electronic.
      // {{lockbox}} carries EVERY box on one line, each with its "Where It Is" note ("Push Button #27
      // (code 4521) at the Front Door, Electronic at the Back Gate"); the three chips under it carry the
      // FIRST box only, for a template that lays the parts out itself. An Electronic box never shows its
      // serial or pin in any email (utils/lockboxes.js accessLines).
      { key: "lockbox",       display: "Lockbox (auto, every box)", icon: "🔐" },
      { key: "lockboxType",   display: "Lockbox type (first box)",   icon: "🔐" },
      { key: "lockboxNumber", display: "Lockbox number (first box)", icon: "🔢" },
      { key: "lockboxCode",   display: "Lockbox code (first box)",   icon: "🔑" },
      // Seller Provided Door Code (Henry, 2026-09-18). When the listing has one, the lockbox chips
      // above go blank and this carries the code (sellerDoorCode.js withDoorCode).
      { key: "doorCode",      display: "Door code",      icon: "🔑" },
    ],
  },
];

export function placeholdersFor(templateId) {
  // Custom (user-created) templates get the general Transaction placeholder set
  // (greeting, address, client/seller/buyer names, agent) plus any un-scoped groups.
  const isCustom = typeof templateId === "string" && templateId.startsWith("custom-");
  return PLACEHOLDER_GROUPS.filter(
    (g) =>
      !g.templates ||
      g.templates.includes(templateId) ||
      (isCustom && g.label === "Transaction"),
  );
}
